import {
    IonAccordion,
    IonIcon,
    IonItem,
    IonLabel,
    IonText,
    useIonAlert,
} from '@ionic/react'
import { informationCircleOutline } from 'ionicons/icons'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../../components/Button'
import ContactPartner from '../../../components/Contact/ContactPartner'
import type { ContactBlockedUserFragment } from '../../../lib/apollo/types'
import {
    useContactBlockedUsersQuery,
    useUnblockContactUserMutation,
} from '../../../lib/apollo/types'
import useToast from '../../../providers/Toast/hooks'

import './style.scss'

/**
 * Der einzige Weg zurueck aus einer Blockierung: Die blockierte Unterhaltung
 * ist im Posteingang ausgeblendet und auch ueber ihre Adresse nicht mehr zu
 * oeffnen. Ohne diese Liste waere jede Blockierung endgueltig.
 */
const BlockedContacts: React.FC = () => {
    const { t } = useTranslation()
    const [present] = useToast()
    const [confirm] = useIonAlert()

    const { data, loading } = useContactBlockedUsersQuery()

    const [unblock] = useUnblockContactUserMutation({
        // Die Unterhaltungen kommen mit dem Aufheben zurueck — sie waren nur
        // ausgeblendet.
        refetchQueries: [
            'contactBlockedUsers',
            'contactConversations',
            'contactUnread',
            'contactRequests',
            'contactRequestsWithPeople',
        ],
        onError: () => {
            present(
                t(
                    'settings.blockedContacts.failed',
                    'Die Blockierung konnte nicht aufgehoben werden.',
                ),
                'danger',
            )
        },
    })

    const nameOf = useCallback(
        (blocked: ContactBlockedUserFragment) =>
            blocked.user
                ? `${blocked.user.firstName} ${blocked.user.lastName}`
                : t('settings.blockedContacts.unknown', 'Unbekannter Kontakt'),
        [t],
    )

    const onUnblock = useCallback(
        (blocked: ContactBlockedUserFragment) => {
            confirm({
                header: t(
                    'settings.blockedContacts.unblock',
                    'Blockierung aufheben',
                ),
                message: t('settings.blockedContacts.unblockConfirm', {
                    defaultValue:
                        '{{name}} kann dir danach wieder schreiben, und eure Unterhaltung erscheint wieder in deinem Posteingang.',
                    name: nameOf(blocked),
                }),
                buttons: [
                    { text: t('buttons.cancel'), role: 'cancel' },
                    {
                        text: t(
                            'settings.blockedContacts.unblock',
                            'Blockierung aufheben',
                        ),
                        handler: async () => {
                            await unblock({
                                variables: { userId: blocked.userId },
                            })
                            present(
                                t(
                                    'settings.blockedContacts.unblocked',
                                    'Blockierung aufgehoben.',
                                ),
                                'success',
                            )
                        },
                    },
                ],
            })
        },
        [confirm, t, nameOf, unblock, present],
    )

    const blocked = data?.contactBlockedUsers ?? []

    return (
        <IonAccordion
            className='blocked-contacts'
            value='blocked-contacts'
        >
            <IonItem slot='header'>
                <IonLabel>
                    {t('settings.blockedContacts.title', 'Blockierte Kontakte')}
                </IonLabel>
            </IonItem>

            <IonItem
                lines='none'
                slot='content'
            >
                <IonIcon
                    icon={informationCircleOutline}
                    slot='start'
                />
                <IonLabel className='ion-text-wrap'>
                    <IonText color='medium'>
                        {t(
                            'settings.blockedContacts.description',
                            'Blockierte Kontakte können dir nicht schreiben, und ihre Unterhaltungen sind aus deinem Posteingang ausgeblendet.',
                        )}
                    </IonText>
                </IonLabel>
            </IonItem>

            {!loading && blocked.length === 0 && (
                <IonItem
                    lines='none'
                    slot='content'
                >
                    <IonLabel className='ion-text-wrap'>
                        <IonText color='medium'>
                            {t(
                                'settings.blockedContacts.empty',
                                'Du hast niemanden blockiert.',
                            )}
                        </IonText>
                    </IonLabel>
                </IonItem>
            )}

            {blocked.map((entry) => (
                <IonItem
                    key={entry.userId}
                    className='blocked-contacts__entry'
                    lines='none'
                    slot='content'
                >
                    <ContactPartner
                        size={32}
                        name={nameOf(entry)}
                        image={entry.user?.image}
                    />
                    <Button
                        slot='end'
                        size='small'
                        fill='clear'
                        onClick={() => onUnblock(entry)}
                    >
                        {t('settings.blockedContacts.unblock', 'Blockierung aufheben')}
                    </Button>
                </IonItem>
            ))}
        </IonAccordion>
    )
}

export default BlockedContacts
